import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { clamp } from "../utils/clamp";
import { getPseudoStyleInteger, getStyleInteger } from "../utils/style-utils";

const ToggleSwitchElement = styled.label`
  --thumb-size: 1rem;
  --thumb: var(--text);
  --thumb-highlight: hsl(0 0% 0% / 25%);

  --track-size: calc(var(--thumb-size) * 2);
  --track-padding: 2px;
  --track-inactive: hsl(80 0% 80%);
  --track-active: hsl(80 60% 45%);

  --thumb-color: var(--thumb);
  --thumb-color-highlight: var(--thumb-highlight);
  --track-color-inactive: hsla(var(--backgroundHSL), 0.5);
  --track-color-active: var(--success);

  &[data-appearance="primary"] {
    --track-color-active: var(--primary);
  }
  &[data-appearance="secondary"] {
    --track-color-active: var(--secondary);
  }

  --isLTR: 1;

  display: flex;
  align-items: center;
  gap: 1ch;
  justify-content: space-between;

  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  &[data-size="small"] {
    font-size: 12px;
  }
`;

const Input = styled.input`
    --thumb-position: 0%;
    --thumb-transition-duration: .25s;

    padding: var(--track-padding);
    background: var(--track-color-inactive);
    inline-size: var(--track-size);
    block-size: var(--thumb-size);
    border-radius: var(--track-size);

    appearance: none;
    pointer-events: none;
    touch-action: pan-y;
    border: none;
    outline-offset: 5px;
    box-sizing: content-box;

    flex-shrink: 0;
    display: grid;
    align-items: center;
    grid: [track] 1fr / [track] 1fr;

    transition: background-color .25s ease;

    &::before {
      --highlight-size: 0;

      content: "";
      cursor: pointer;
      pointer-events: auto;
      grid-area: track;
      inline-size: var(--thumb-size);
      block-size: var(--thumb-size);
      background: var(--thumb-color);
      box-shadow: 0 0 0 var(--highlight-size) var(--thumb-color-highlight);
      border-radius: 50%;
      transform: translateX(var(--thumb-position));
      transition: transform var(--thumb-transition-duration) ease,
          box-shadow .25s ease;
    }

    &:not(:disabled):hover::before {
      --highlight-size: .5rem;
    }

    &:checked {
      background: var(--track-color-active);
      --thumb-position: calc((var(--track-size) - 100%) * var(--isLTR));
    }

    &:indeterminate {
      --thumb-position: calc(
        calc(calc(var(--track-size) / 2) - calc(var(--thumb-size) / 2))
        * var(--isLTR)
      );
    }

    &:disabled {
      cursor: not-allowed;
      --thumb-color: transparent;

      &::before {
        cursor: not-allowed;
        box-shadow: inset 0 0 0 2px hsl(0 0% 100% / 50%);
      }
    }
  }

`;

// TODO add drag support (touch)
const ToggleSwitch: React.FC<
  {
    label: React.ReactNode;
    size?: "default" | "small";
    appearance?: "default" | "primary" | "secondary";
  } & React.ComponentPropsWithoutRef<typeof Input>
> = ({
  label,
  size = "default",
  appearance = "default",
  onChange,
  ...props
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const handleChange: React.ChangeEventHandler = (e) => {
    onChange(e);
  };
  useEffect(() => {
    let isDragging = false;
    let recentlyDragged = false;
    let thumbSize = 0;
    let padding = 0;
    let bounds = {
      lower: 0,
      middle: 0,
      upper: 0
    };
    if (ref.current) {
      const el = ref.current;
      thumbSize = getPseudoStyleInteger(el, "width");
      padding =
        getStyleInteger(el, "padding-left") +
        getStyleInteger(el, "padding-right");
      bounds = {
        lower: 0,
        middle: (el.clientWidth - padding) / 4,
        upper: el.clientWidth - thumbSize - padding
      };

      const shouldSetChecked = () => {
        const propVal = el.style.getPropertyValue("--thumb-position");
        let curpos = Math.abs(Number.parseInt(propVal, 10));
        if (!curpos) {
          curpos = el.checked ? bounds.lower : bounds.upper;
        }

        return curpos >= bounds.middle;
      };
      const dragStart = () => {
        if (el.disabled) {
          return;
        }
        isDragging = true;
        el.addEventListener("pointermove", dragMove);
        el.style.setProperty("--thumb-transition-duration", "0s");
      };
      const dragMove = (e: PointerEvent) => {
        const pos = clamp(
          Math.round(e.offsetX - thumbSize / 2),
          bounds.lower,
          bounds.upper
        );
        el.style.setProperty("--thumb-position", `${pos}px`);
      };
      const dragEnd = () => {
        if (!isDragging) {
          return;
        }
        isDragging = false;
        el.removeEventListener("pointermove", dragMove);

        const wasChecked = el.checked;
        const nextChecked = shouldSetChecked();
        if (wasChecked !== nextChecked) {
          const event = document.createEvent("MouseEvents");
          event.initEvent("click", true, true);
          el.dispatchEvent(event);
        }

        el.style.removeProperty("--thumb-transition-duration");
        el.style.removeProperty("--thumb-position");

        recentlyDragged = true;
        setTimeout(() => (recentlyDragged = false), 300);
      };
      const preventClick = (e: MouseEvent) => {
        if (recentlyDragged) {
          e.preventDefault();
          e.stopPropagation();
        }
      };
      el.addEventListener("pointerdown", dragStart);
      el.addEventListener("pointerup", dragEnd);
      el.addEventListener("click", preventClick);
      window.addEventListener("pointerup", dragEnd);
      () => {
        el.removeEventListener("pointerdown", dragStart);
        el.removeEventListener("pointerup", dragEnd);
        el.removeEventListener("click", preventClick);
        window.removeEventListener("pointerup", dragEnd);
      };
    }
  }, [ref.current]);
  return (
    <ToggleSwitchElement
      data-control
      data-size={size}
      data-appearance={appearance}
    >
      {label}
      <Input ref={ref} type="checkbox" onChange={handleChange} {...props} />
    </ToggleSwitchElement>
  );
};

export default ToggleSwitch;
